import { useState } from "react";

import { upsertAdminAccessRequestInternalNote } from "@/lib/api/admin-access-requests";
import type { AdminAccessRequest } from "@/lib/api/admin-access-requests";
import { formatInternalNoteAudit } from "@/lib/api/admin-access-request-labels";
import { parseApiError } from "@/lib/api/api-error";

const MAX_INTERNAL_NOTE_LENGTH = 1000;

type NoteMode = "empty" | "view" | "edit";

type InternalNoteSectionProps = {
  request: AdminAccessRequest;
  onUpdated: (request: AdminAccessRequest) => void;
};

function initialMode(request: AdminAccessRequest): NoteMode {
  return request.internal_note?.trim() ? "view" : "empty";
}

export function InternalNoteSection({
  request,
  onUpdated,
}: InternalNoteSectionProps) {
  const [mode, setMode] = useState<NoteMode>(() => initialMode(request));
  const [draft, setDraft] = useState(request.internal_note ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const noteText = request.internal_note?.trim() ?? "";
  const audit = formatInternalNoteAudit({
    createdByName: request.internal_note_created_by?.name,
    createdAt: request.internal_note_created_at,
    updatedByName: request.internal_note_updated_by?.name,
    updatedAt: request.internal_note_updated_at,
  });

  async function saveNote() {
    if (saving) {
      return;
    }
    if (draft.length > MAX_INTERNAL_NOTE_LENGTH) {
      setError(`Notes must be ${MAX_INTERNAL_NOTE_LENGTH} characters or fewer.`);
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const updated = await upsertAdminAccessRequestInternalNote(
        request.id,
        draft,
      );
      onUpdated(updated);
      setMode(updated.internal_note?.trim() ? "view" : "empty");
      setDraft(updated.internal_note ?? "");
    } catch (err) {
      const parsed = parseApiError(err);
      setError(
        parsed.message?.trim() ||
          "We couldn't save the note. Please try again.",
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="notes-section" data-testid="admin-internal-note">
      <div className="dlabel">Internal notes</div>

      {mode === "empty" ? (
        <button
          type="button"
          className="filter-btn notes-add-btn"
          data-testid="admin-note-add"
          onClick={() => {
            setDraft("");
            setError(null);
            setMode("edit");
          }}
        >
          <i className="ti ti-plus" aria-hidden="true" />
          Add note
        </button>
      ) : null}

      {mode === "view" ? (
        <div className="notes-view" data-testid="admin-note-view">
          <p className="notes-body">{noteText}</p>
          {audit ? (
            <p className="notes-audit" data-testid="admin-note-audit">
              {audit}
            </p>
          ) : null}
          <button
            type="button"
            className="filter-btn notes-edit-btn"
            data-testid="admin-note-edit"
            onClick={() => {
              setDraft(request.internal_note ?? "");
              setError(null);
              setMode("edit");
            }}
          >
            <i className="ti ti-pencil" aria-hidden="true" />
            Edit
          </button>
        </div>
      ) : null}

      {mode === "edit" ? (
        <div className="notes-edit" data-testid="admin-note-edit-form">
          <textarea
            className="notes-area"
            data-testid="admin-note-textarea"
            value={draft}
            maxLength={MAX_INTERNAL_NOTE_LENGTH}
            placeholder="Add internal notes visible to the onboarding team only…"
            rows={4}
            disabled={saving}
            onChange={(event) => setDraft(event.target.value)}
          />
          <div className="notes-actions">
            <button
              type="button"
              className="filter-btn on"
              data-testid="admin-note-save"
              disabled={saving}
              onClick={() => {
                void saveNote();
              }}
            >
              {saving
                ? "Saving…"
                : noteText
                  ? "Update note"
                  : "Save note"}
            </button>
            {noteText ? (
              <button
                type="button"
                className="filter-btn"
                data-testid="admin-note-cancel"
                disabled={saving}
                onClick={() => {
                  setDraft(request.internal_note ?? "");
                  setError(null);
                  setMode("view");
                }}
              >
                Cancel
              </button>
            ) : (
              <button
                type="button"
                className="filter-btn"
                data-testid="admin-note-cancel"
                disabled={saving}
                onClick={() => {
                  setDraft("");
                  setError(null);
                  setMode("empty");
                }}
              >
                Cancel
              </button>
            )}
          </div>
        </div>
      ) : null}

      {error ? (
        <p className="notes-error" data-testid="admin-note-error" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
